import { Api } from 'lavva.exalushome';
import { DataFrame, Method, Status } from 'lavva.exalushome/build/js/DataFrame';
import { DeviceResponseType, IDevice } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { ExalusConnectionService } from "lavva.exalushome/build/js/Services/ExalusConnectionService";
import { ResponseResult } from 'lavva.exalushome/build/js/Services/FieldChangeResult';
import { IExalusConnectionService } from "lavva.exalushome/build/js/Services/IExalusConnectionService";
import { ILoggerService } from 'lavva.exalushome/build/js/Services/Logging/ILoggerService';
import { LoggerService } from 'lavva.exalushome/build/js/Services/Logging/LoggerService';
import { BlindActionEnum, FacadeActionEnum, GetDeviceState, SetBlindPosition, SetFacadePosition } from "lavva.exalushome/build/js/Services/Devices/Tasks/Tasks";
import { DeviceTaskExecutionResult } from "lavva.exalushome/build/js/Services/Devices/TaskExecutionResult";
import { ControllerConfigurationService } from "lavva.exalushome/build/js/Services/Controller/ControllerConfigurationService"
import { IControllerConfigurationService } from "lavva.exalushome/build/js/Services/Controller/IControllerConfigurationService"
import { GetChannelParamsRequest, GetParamsRequest } from '../Common/DataRequest';
import { IZfBidiConfigService } from './IZfBidiConfigService';
import { CalibrationProgress, CalibrationStatusData, LamelaTiltCalibration, FacadeLockTime, PairedWindSensor, FacadeDetection } from './ZfBidiConfigParams';
import { FacadePositionState, IDeviceState } from 'lavva.exalushome/build/js/Services/Devices/IDeviceState';
import { IAppStateService } from "lavva.exalushome/build/js/Services/IAppStateService";
import { AppStateService } from "lavva.exalushome/build/js/Services/AppStateService";

export class ZfBidiConfigService implements IZfBidiConfigService {

    private _connection: IExalusConnectionService | null = null;
    private _logger: ILoggerService;
    private _controllerConfigurationService: IControllerConfigurationService;
    private _lamelaCalibrationContext: LamelaCalibrationContext = new LamelaCalibrationContext();
    public static readonly ServiceName: string = "ZfBidiConfigService";

    constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
        this._controllerConfigurationService = Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName);
        this._logger = Api.Get<ILoggerService>(LoggerService.ServiceName);
    }

    GetServiceName(): string {
        return ZfBidiConfigService.ServiceName;
    }

    private IsFacadeConfigurationError(value: boolean | ResponseResult<FacadeConfigurationErrorCode>): value is ResponseResult<FacadeConfigurationErrorCode> {
        return typeof value === "object" && value !== null && ("_type" in value || "Type" in value);
    }

    async RunAutomaticLamelaPositionCalibrationAsync(device: IDevice, channel: number): Promise<CalibrationStatusData | ResponseResult<FacadeConfigurationErrorCode>> {
        const appStateService = Api.Get<IAppStateService>(AppStateService.ServiceName);
        
        try {
            if (!device.Channels.any(ch => ch.Number === channel)) {
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.InvalidChannelNumber, `Cannot calibrate facade blind - invalid channel number: ${channel}`);
            }

            const currentCalibrationStatus = await this.GetCalibrationStatusAsync(device, channel);

            if ((currentCalibrationStatus as ResponseResult<FacadeConfigurationErrorCode>).Type != null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot calibrate facade blind - unable to get current calibration status.`);
            else if ((currentCalibrationStatus as CalibrationStatusData).CalibrationProgress != CalibrationProgress.Disabled)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.CalibrationsIsCurrentlyRunning, `Cannot calibrate facade blind - calibration of device is currently running.`);

            const task = new SetFacadePosition();
            task.Position = FacadeActionEnum.Stop;

            switch (await device.Channels.first(ch => ch.Number == channel).ExecuteTaskAsync(task)) {
                case DeviceTaskExecutionResult.Executed:
                    break;
                default:
                    return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.DeviceNotResponding, `Cannot calibrate facade blind - device is not responding.`);
            }

            await this._controllerConfigurationService?.EnterConfigurationModeAsync();
            const calibrationReq = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new EnableAutoCalibraionRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (calibrationReq == null || calibrationReq.Status == null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot calibrate facade blind - calibration request failed for an unknown reason.`);


            if (calibrationReq.Status !== Status.OK)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot calibrate facade blind - calibration request failed - request status code: ${calibrationReq.Status}`);

            appStateService.DisallowHibernation();
            const currentCalibrationTask = new Promise<FacadeConfigurationErrorCode | CalibrationStatusData>((resolve, reject) => {
                let failedCount = 0;
                const calibrationStatusCheck = setInterval(async () => {
                    var currentStatus = await this.GetCalibrationStatusAsync(device, channel);

                    if ((currentStatus as ResponseResult<FacadeConfigurationErrorCode>).Type != null) {
                        failedCount++;
                        if (failedCount > 6) {
                            clearInterval(calibrationStatusCheck);
                            resolve(FacadeConfigurationErrorCode.DeviceNotResponding);
                        }
                    } else {
                        switch ((currentStatus as CalibrationStatusData).CalibrationProgress) {
                            case CalibrationProgress.MovementTimeCalibration:
                                this._logger.Debug(`Facade blind calibration in progress...`);
                                failedCount = 0;
                                break;
                            case CalibrationProgress.LamellaTiltCalibration:
                                resolve(FacadeConfigurationErrorCode.CalibrationsIsCurrentlyRunning);
                                break;
                            default:
                                this._logger.Debug(`Facade blind calibration finished!`);
                                clearInterval(calibrationStatusCheck);
                                resolve(currentStatus as CalibrationStatusData);
                                break;
                        }
                    }
                }, 2000);
            })

            const result = await currentCalibrationTask;
            await this.StopCalibrationAsync(device, channel);
            appStateService.AllowHibernation();

            if ((result as CalibrationStatusData).CalibrationProgress == null) {
                switch (result as FacadeConfigurationErrorCode) {
                    case FacadeConfigurationErrorCode.DeviceNotResponding:
                        return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.DeviceNotResponding, `Facade calibration canceled - device not responding!`);
                    case FacadeConfigurationErrorCode.CalibrationsIsCurrentlyRunning:
                        return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.CalibrationsIsCurrentlyRunning, `Cannot calibrate facade blind - calibration of device is currently running.`);
                }
            }

            return result as CalibrationStatusData;
        } catch (error) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! RunAutomaticLamelaPositionCalibrationAsync()`);
            this._controllerConfigurationService?.ExitConfigurationModeAsync();
            appStateService.AllowHibernation();
            this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot calibrate facade blind. ${error}`);

            return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot calibrate facade blind - unknown error.`)
        }
    }

    async StopCalibrationAsync(device: IDevice, channel: number): Promise<Status> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return Status.WrongData;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new StopCalibrationRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(`Failed to stop calibration - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(`Failed to stop calibration - response status: ${result.Status}`);

            this._logger.Error(ZfBidiConfigService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! StopCalibrationAsync()`);
            this._controllerConfigurationService?.ExitConfigurationModeAsync();
            return result.Status;

        } catch (error) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot stop facade blind calibration. ${error}`);
            return Status.FatalError;
        }
    }

    LamelaTiltCalibration(device: IDevice, channel: number): LamelaTiltCalibration | ResponseResult<FacadeConfigurationErrorCode> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.InvalidChannelNumber, `Cannot get lamela tilt calibration functions - invalid channel number, channel: ${channel}`);

            const calibrationFunctions: LamelaTiltCalibration = {
                InitCalibrationAsync: async (): Promise<ResponseResult<FacadeConfigurationErrorCode> | Status> => {
                    const maxSetPositionWaitTime = 120000;
                    const devChannel = device.Channels.first(ch => ch.Number == channel);
                    const currentCalibrationStatus = await this.GetCalibrationStatusAsync(device, channel);
                    this._lamelaCalibrationContext = new LamelaCalibrationContext();

                    if ((currentCalibrationStatus as ResponseResult<FacadeConfigurationErrorCode>).Type != null)
                        return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot calibrate facade blind - unable to get current calibration status.`);
                    else if ((currentCalibrationStatus as CalibrationStatusData).CalibrationProgress != CalibrationProgress.Disabled)
                        return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.CalibrationsIsCurrentlyRunning, `Cannot calibrate facade blind - calibration of device is currently running.`);
                    else if((currentCalibrationStatus as CalibrationStatusData).TravelTimeCalibration = false)
                        return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.TravelTimeCalibrationNotValid, `Cannot calibrate facade blind - please preform travel time calibration first by running automatic position calibration.`);

                    const facadeStop = new SetFacadePosition();
                    facadeStop.FacadeAction = FacadeActionEnum.Stop;

                    if (await devChannel.ExecuteTaskAsync(facadeStop) != DeviceTaskExecutionResult.Executed)
                        return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot calibrate facade blind - unable to stop blind.`);

                    const facadePosition = new SetFacadePosition();
                    facadePosition.FacadeAction = FacadeActionEnum.TiltAndPercentage;
                    facadePosition.Position = 80;
                    facadePosition.Tilt = 100;

                    if (await devChannel.ExecuteTaskAsync(facadePosition) != DeviceTaskExecutionResult.Executed)
                        return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot calibrate facade blind - unable to set correct position.`);

                    const setPositionTask = new Promise<FacadePositionState | ResponseResult<FacadeConfigurationErrorCode>>((resolve, reject) => {
                        const timeout = Date.now() + maxSetPositionWaitTime;

                        const statusInterval = setInterval(() => {
                            this._logger.Debug(ZfBidiConfigService.ServiceName, `Lamela tilt calibration initialization - seting blind to calibration position...`);
                            if (Date.now() > timeout) {
                                device.OnDeviceStateRefreshedOrChangedEvent().Unsubscribe(PositionStateChangeHandler);
                                clearInterval(statusInterval);
                                reject(new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.DeviceNotResponding, `Cannot calibrate facade blind - unable to get correct blind position for calibration.`));
                            } else
                                devChannel.ExecuteTaskAsync(new GetDeviceState());
                        }, 2000)

                        const PositionStateChangeHandler = (stateChange: IDeviceState<any>) => {
                            if (stateChange.TypeAsEnum == DeviceResponseType.FacadePosition) {
                                if ((stateChange.Data as FacadePositionState).Position == 80) {
                                    this._logger.Debug(ZfBidiConfigService.ServiceName, `Lamela tilt calibration initialization - correct position was set, position: 80%`);
                                    device.OnDeviceStateRefreshedOrChangedEvent().Unsubscribe(PositionStateChangeHandler);
                                    clearInterval(statusInterval);
                                    resolve(stateChange.Data);
                                }
                                else if((stateChange.Data as FacadePositionState).Position == 255) {
                                    device.OnDeviceStateRefreshedOrChangedEvent().Unsubscribe(PositionStateChangeHandler);
                                    clearInterval(statusInterval);
                                    reject(new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.TravelTimeCalibrationNotValid, `Cannot calibrate facade blind - please preform travel time calibration first by running automatic position calibration.`));
                                }
                            }
                        }

                        device.OnDeviceStateRefreshedOrChangedEvent().Subscribe(PositionStateChangeHandler);
                    })

                    try {
                        await setPositionTask;
                    } catch (error) {
                        return error as ResponseResult<FacadeConfigurationErrorCode>;
                    }

                    await this._controllerConfigurationService?.EnterConfigurationModeAsync();
                    const startCalibrationResult = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new StartTiltCalibrationRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

                    if (startCalibrationResult == null || startCalibrationResult.Status == null)
                        return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot calibrate facade blind - unable to start calibration.`);

                    if (startCalibrationResult.Status !== Status.OK)
                        return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot calibrate facade blind - unable to start calibration - response status: ${startCalibrationResult.Status}`);

                    this._lamelaCalibrationContext.CalibrationInitialized = true;
                    return Status.OK;
                },
                LamelaManualCalibration: {
                    MoveOneStepDownAsync: async (): Promise<Status> => {
                        const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new OneStepDownRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

                        if (result == null || result.Status == null)
                            return Status.FatalError;

                        return result.Status;
                    },
                    MoveOneStepUpAsync: async (): Promise<Status> => {
                        const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new OneStepUpRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

                        if (result == null || result.Status == null)
                            return Status.FatalError;

                        return result.Status;
                    }
                },
                ConfirmFirstPositionAsync: async (): Promise<Status> => {
                    try {
                        if (!this._lamelaCalibrationContext.CalibrationInitialized)
                            return Status.OperationNotPermitted;

                        const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new ConfirmFirstTiltPositionRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

                        if (result == null || result.Status == null)
                            return Status.FatalError;

                        if (result.Status == Status.OK)
                            this._lamelaCalibrationContext.FirstPositionConfirmed = true;

                        return result.Status;
                    } catch (error) {
                        this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot confirm first tilt lamela position. ${error}`);
                        return Status.FatalError;
                    }
                },
                ConfirmSecondPositionAsync: async (): Promise<Status> => {
                    try {
                        if (!this._lamelaCalibrationContext.CalibrationInitialized || !this._lamelaCalibrationContext.FirstPositionConfirmed)
                            return Status.OperationNotPermitted;

                        const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new ConfirmSecondTiltPositionRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

                        if (result == null || result.Status == null)
                            return Status.FatalError;

                        if (result.Status == Status.OK)
                            this._lamelaCalibrationContext.FirstPositionConfirmed = true;

                        return result.Status;
                    } catch (error) {
                        this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot confirm second tilt lamela position. ${error}`);
                        return Status.FatalError;
                    }
                },
                EndCalibrationAsync: async (): Promise<Status> => {
                    try {
                        this._lamelaCalibrationContext = new LamelaCalibrationContext();
                        const stopCalibrationResult = await this.StopCalibrationAsync(device, channel);

                        if (stopCalibrationResult != Status.OK)
                            return Status.Error;

                        const facadeUp = new SetFacadePosition();
                        facadeUp.FacadeAction = FacadeActionEnum.Open;

                        device.ExecuteDeviceTaskAsync(facadeUp);
                        this._connection?.SendAndWaitForResponseAsync(new GetCalibrationConfigurationRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

                        return Status.OK;
                    } catch (error) {
                        this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot finish lamela tilt calibration. ${error}`);
                        return Status.FatalError;
                    }
                }
            }

            return calibrationFunctions;
        } catch (error) {
            return new ResponseResult(FacadeConfigurationErrorCode.OtherError, `Cannot get lamela tilt calibration functions - unknown error.`);
        }
    }

    async GetCalibrationStatusAsync(device: IDevice, channel: number): Promise<CalibrationStatusData | ResponseResult<FacadeConfigurationErrorCode>> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.InvalidChannelNumber, `Cannot get facade calibration status - invalid channel number: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<CalibrationStatusData>(new GetCalibrationStatusRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot get facade calibration status - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot get facade calibration status - controller responded with error code: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.NoData, `Cannot get facade calibration status - no data in response.`);

            return result.Data;
        } catch (error) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot get facade calibration status. ${error}`);
            return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot get facade calibration status - unknown error.`);
        }
    }

    async ChangeMovementDirectionAsync(device: IDevice, channel: number): Promise<Status> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return Status.WrongData;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new ChangeMovementDirectionRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(`Failed to change movement direction - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(`Failed to change movement direction - response status: ${result.Status}`);

            return result.Status;

        } catch (error) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot change movement direction. ${error}`);
            return Status.FatalError;
        }
    }

    async IsWindSensorsPairedAsync(device: IDevice, channel: number): Promise<boolean | ResponseResult<FacadeConfigurationErrorCode>> {
        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<PairedWindSensor>(new IsAnyWindSensorsPairedRequest(device.Guid), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot check if any wind sensor is paired - unknown error.`);

            if (result.Status !== Status.OK || result.Data == null)
                return false;

            return result.Data.IsPaired;
        } catch (error) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot check if any wind sensor is paired. ${error}`);
            return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot check if any wind sensor is paired - unknown error.`);
        }
    }

    async GetWindSensorActiveAsync(device: IDevice, channel: number): Promise<boolean | ResponseResult<FacadeConfigurationErrorCode>> {
        try {
            const paired = await this.IsWindSensorsPairedAsync(device, channel);

            if (this.IsFacadeConfigurationError(paired))
                return paired;

            if (!paired)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.WindSensorNotPaired, `Cannot get wind sensor lock time - wind sensor not paired.`);

            const result = await this._connection?.SendAndWaitForResponseAsync<boolean>(new GetDetectionWindSensorActiveRequest(device.Guid), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot check if 'wind detection lock' functionality is active - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot check if 'wind detection lock' functionality is active - response status: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.NoData, `Cannot check if 'wind detection lock' - no data in response.`);

            return result.Data;
        } catch (error) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot check if 'wind detection lock' functionality is active. ${error}`);
            return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot check if 'wind detection lock' functionality is active - unknown error.`);
        }
    }

    async SetWindSensorActiveAsync(device: IDevice, channel: number, windDetectionLockActive: boolean): Promise<Status> {
        const paired = await this.IsWindSensorsPairedAsync(device, channel);

        if (this.IsFacadeConfigurationError(paired))
            return Status.FatalError;

        if (!paired)
            return Status.ResourceIsNotAvailable;

        const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetDetectionWindSensorActiveRequest(device.Guid, windDetectionLockActive), 8000, false);

        if (result == null || result.Status == null) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `Failed to change device parameters - unknow error.`);
            return Status.FatalError;
        }

        if (result.Status !== Status.OK)
            this._logger.Error(ZfBidiConfigService.ServiceName, `Failed to change device parameters, response status: ${result.Status}`);

        return result.Status;
    }

    async GetWindSensorLockTimeAsync(device: IDevice, channel: number): Promise<FacadeLockTime | ResponseResult<FacadeConfigurationErrorCode>> {
        try {
            const paired = await this.IsWindSensorsPairedAsync(device, channel);

            if (this.IsFacadeConfigurationError(paired))
                return paired;

            if (!paired)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.WindSensorNotPaired, `Cannot get wind sensor lock time - wind sensor not paired.`);

            const result = await this._connection?.SendAndWaitForResponseAsync<FacadeLockTime>(new GetDetectionWindSensorLockTimeRequest(device.Guid), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot get wind sensor lock time - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot get wind sensor lock time - response status: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.NoData, `Cannot get wind sensor lock time - no data in response.`);

            return result.Data;
        } catch (error) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot get wind sensor lock time. ${error}`);
            return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot get wind sensor lock time - unknown error.`);
        }
    }

    async SetWindSensorLockTimeAsync(device: IDevice, channel: number, lockTime: FacadeLockTime): Promise<Status> {
        try {
            const paired = await this.IsWindSensorsPairedAsync(device, channel);

            if (this.IsFacadeConfigurationError(paired))
                return Status.FatalError;

            if (!paired)
                return Status.ResourceIsNotAvailable;

            const result = await this._connection?.SendAndWaitForResponseAsync<FacadeLockTime>(new SetDetectionWindSensorLockTimeRequest(new FacadeLockTimeFrame(device.Guid, lockTime)), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(ZfBidiConfigService.ServiceName, `Failed to set wind sensor lock time - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(ZfBidiConfigService.ServiceName, `Failed to set wind sensor lock time, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot set wind sensor lock time. ${error}`);
            return Status.FatalError;
        }
    }

    async IsBlindLockedByWindSensorAsync(device: IDevice, channel: number): Promise<boolean | ResponseResult<FacadeConfigurationErrorCode>> {
        try {
            const paired = await this.IsWindSensorsPairedAsync(device, channel);

            if (this.IsFacadeConfigurationError(paired))
                return paired;

            if (!paired)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.WindSensorNotPaired, `Cannot check if blind is blocked - wind sensor not paired.`);

            const result = await this._connection?.SendAndWaitForResponseAsync<boolean>(new IsBlockedByWindSensorRequest(device.Guid), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot check if blind is blocked - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot check if blind is blocked - response status: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.NoData, `Cannot check if blind is blocked - no data in response.`);

            return result.Data;
        } catch (error) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot check if blind is blocked. ${error}`);
            return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot check if blind is blocked - unknown error.`);
        }
    }

    async ClearBlindLockByWindSensorAsync(device: IDevice, channel: number): Promise<Status> {
        try {
            const paired = await this.IsWindSensorsPairedAsync(device, channel);

            if (this.IsFacadeConfigurationError(paired))
                return Status.FatalError;

            if (!paired)
                return Status.ResourceIsNotAvailable;

            const result = await this._connection?.SendAndWaitForResponseAsync<boolean>(new ClearBlindLockByWindSensorRequest(device.Guid), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(ZfBidiConfigService.ServiceName, `Failed to change device parameters - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(ZfBidiConfigService.ServiceName, `Failed to change device parameters, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot clear wind blockade state. ${error}`);
            return Status.FatalError;
        }
    }

    async GetFacadeTypeDetectionAsync(device: IDevice, channel: number): Promise<FacadeDetection | ResponseResult<FacadeConfigurationErrorCode>> {
        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<FacadeTypeDetectionFrame>(new GetFacadeTypeDetectionRequest(new GetParamsRequest(device.Guid)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot get facade detection settings - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot get facade detection settings - controller responded with error code: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.NoData, `Cannot get facade detection settings - no data in response.`);

            return result.Data.FacadeDetection;
        } catch (error) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot get facade detection settings. ${error}`);
            return new ResponseResult<FacadeConfigurationErrorCode>(FacadeConfigurationErrorCode.OtherError, `Cannot get facade detection settings - unknown error.`);
        }
    }

    async SetFacadeTypeDetectionAsync(device: IDevice, channel: number, detectionType: FacadeDetection): Promise<Status> {
        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<any>(new SetFacadeTypeDetectionRequest(new FacadeTypeDetectionFrame(device.Guid, detectionType)), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(ZfBidiConfigService.ServiceName, `Failed to facade detection settings - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(ZfBidiConfigService.ServiceName, `Failed to facade detection settings, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            this._logger.Error(ZfBidiConfigService.ServiceName, `Cannot facade detection settings. ${error}`);
            return Status.FatalError;
        }
    }
}

export enum FacadeConfigurationErrorCode {
    InvalidChannelNumber = "InvalidChannelNumber",
    OtherError = "OtherError",
    CalibrationsIsCurrentlyRunning = "CalibrationsIsCurrentlyRunning",
    DeviceNotResponding = "DeviceNotResponding",
    WindSensorNotPaired = "WindSensorNotPaired",
    TravelTimeCalibrationNotValid = "TravelTimeCalibrationNotValid",
    NoData = "NoData"
}

//change direction
class ChangeMovementDirectionRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/facade/direction/reverse";
        this.Method = Method.Post;
        this.Data = data;
    }
}

//calibration
class GetCalibrationStatusRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/facade/calibration/status";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class EnableAutoCalibraionRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/facade/calibration/start";
        this.Method = Method.Post;
        this.Data = data;
    }
}

class StopCalibrationRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/facade/calibration/stop";
        this.Method = Method.Post;
        this.Data = data;
    }
}

class StartTiltCalibrationRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/facade/lamella/tilt/calibration/start";
        this.Method = Method.Post;
        this.Data = data;
    }
}

class OneStepUpRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/facade/calibration/step/up";
        this.Method = Method.Post;
        this.Data = data;
    }
}

class OneStepDownRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/facade/calibration/step/down";
        this.Method = Method.Post;
        this.Data = data;
    }
}

class ConfirmFirstTiltPositionRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/facade/calibration/lamella/position/first/confirm";
        this.Method = Method.Post;
        this.Data = data;
    }
}

class ConfirmSecondTiltPositionRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/facade/calibration/lamella/position/second/confirm";
        this.Method = Method.Post;
        this.Data = data;
    }
}

class GetCalibrationConfigurationRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/facade/calibration/configuration";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class LamelaCalibrationContext {
    CalibrationInitialized: boolean = false;
    FirstPositionConfirmed: boolean = false;
    SeconfPositionConfirmed: boolean = false;
}

//WindSensor
class IsAnyWindSensorsPairedRequest extends DataFrame<string> {
    constructor(guid: string) {
        super();
        this.Resource = "/extalife/device/parameters/facade/sensor/paired";
        this.Method = Method.Get;
        this.Data = guid;
    }
}

class GetDetectionWindSensorActiveRequest extends DataFrame<string> {
    constructor(guid: string) {
        super();
        this.Resource = "/extalife/device/parameters/wind/sensor/status";
        this.Method = Method.Get;
        this.Data = guid;
    }
}

class SetDetectionWindSensorActiveRequest extends DataFrame<string> {
    constructor(guid: string, activate: boolean) {
        super();
        this.Resource = activate ? "/extalife/device/parameters/wind/activate/" : "/extalife/device/parameters/wind/deactivate/";
        this.Method = Method.Post;
        this.Data = guid;
    }
}

class GetDetectionWindSensorLockTimeRequest extends DataFrame<string> {
    constructor(guid: string) {
        super();
        this.Resource = "/extalife/device/parameters/wind/blockade/time";
        this.Method = Method.Get;
        this.Data = guid;
    }
}

class FacadeLockTimeFrame {
    constructor(guid: string, minutes: FacadeLockTime) {
        this.DeviceGuid = guid;
        this.Minutes = minutes;
    }
    DeviceGuid: string = "";
    Minutes: FacadeLockTime = 0;
}

class SetDetectionWindSensorLockTimeRequest extends DataFrame<FacadeLockTimeFrame> {
    constructor(data: FacadeLockTimeFrame) {
        super();
        this.Resource = "/extalife/device/parameters/wind/blockade/time";
        this.Method = Method.Put;
        this.Data = data;
    }
}

class IsBlockedByWindSensorRequest extends DataFrame<string> {
    constructor(guid: string) {
        super();
        this.Resource = "/extalife/device/parameters/wind/blockade/";
        this.Method = Method.Get;
        this.Data = guid;
    }
}

class ClearBlindLockByWindSensorRequest extends DataFrame<string> {
    constructor(guid: string) {
        super();
        this.Resource = "/extalife/device/parameters/wind/blockade/";
        this.Method = Method.Delete;
        this.Data = guid;
    }
}

//Facade type detection
class FacadeTypeDetectionFrame {
    constructor(guid: string, detection: FacadeDetection) {
        this.DeviceGuid = guid;
        this.FacadeDetection = detection;
    }
    DeviceGuid: string = "";
    FacadeDetection: FacadeDetection = FacadeDetection.Automatic;
}

class GetFacadeTypeDetectionRequest extends DataFrame<GetParamsRequest> {
    constructor(data: GetParamsRequest) {
        super();
        this.Resource = "/extalife/device/facade/type/detection";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class SetFacadeTypeDetectionRequest extends DataFrame<FacadeTypeDetectionFrame> {
    constructor(data: FacadeTypeDetectionFrame) {
        super();
        this.Resource = "/extalife/device/facade/type/detection";
        this.Method = Method.Put;
        this.Data = data;
    }
}
